import { useMemo } from 'react';
import { encodeQr } from '../lib/qrcode';

/**
 * otpauth 链接的二维码。
 *
 * 自研编码器 + 内联 SVG：既不把含密钥的链接交给任何第三方图表服务，
 * 也不引入 canvas 或位图。SVG 在任意缩放下都锐利，窄屏按容器宽度自适应。
 */
export function QRCode({
  value,
  size = 200,
  label,
}: {
  value: string;
  size?: number;
  label?: string;
}) {
  const matrix = useMemo(() => {
    try {
      return encodeQr(value, 'M');
    } catch {
      // 内容超出容量时不要把页面整个搞崩：让调用方退回到「手动输入密钥」。
      return null;
    }
  }, [value]);

  if (!matrix) {
    return (
      <div className="rounded-control bg-warn-soft px-3 py-6 text-center text-[13px] text-fg">
        内容过长，无法生成二维码。请改用下方的密钥手动添加。
      </div>
    );
  }

  const quietZone = 4;
  const side = matrix.size + quietZone * 2;

  // 路径直接写在 viewBox 坐标系里，缩放交给 viewBox —— 再叠一层 transform 会重复放大。
  const cells: string[] = [];
  for (let row = 0; row < matrix.size; row += 1) {
    for (let col = 0; col < matrix.size; col += 1) {
      if (matrix.modules[row * matrix.size + col] !== 1) continue;
      cells.push(`M${col + quietZone} ${row + quietZone}h1v1h-1z`);
    }
  }

  return (
    <svg
      viewBox={`0 0 ${side} ${side}`}
      width="100%"
      height="100%"
      style={{ maxWidth: size, maxHeight: size }}
      role="img"
      aria-label={label ?? '两步验证二维码'}
      className="rounded-control bg-white"
      shapeRendering="crispEdges"
    >
      <rect width={side} height={side} fill="#ffffff" />
      <path d={cells.join('')} fill="#181818" />
    </svg>
  );
}
